// LSP `textDocument/references` → CodeMirror find-references adapter.
//
// The interaction model: Shift-F12 (the VS Code / IntelliJ standard for
// "Find All References") queries the LSP, highlights every reference
// inside the current document with a coloured mark, and parks the
// cursor on the first one. Escape clears the highlights. The plan also
// asks for a right-click menu item — that needs a custom popup
// component (the browser's native context menu isn't customisable),
// which is out of scope for PR 4; flagged as a TODO and revisited
// alongside Pro's toolbar in m6 when we already have a popup primitive
// on hand.
//
// Cross-file references (e.g. via `include` chains) are filtered out
// for the same reason as `lspDefinition` — Free's m5a editor owns one
// synthetic document; surfacing a multi-file reference list belongs with
// Pro's multi-pane shell in m6.

import { EditorSelection, type Extension, StateEffect, StateField } from '@codemirror/state';
import {
    type Command,
    Decoration,
    type DecorationSet,
    EditorView,
    type KeyBinding,
    keymap,
} from '@codemirror/view';
import type { Location } from 'vscode-languageserver-protocol';
import type { LspClient } from '../client.ts';
import { lspRangeToCm } from '../positions.ts';

const setReferencesEffect = StateEffect.define<DecorationSet>();

const referencesField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(value, tr) {
        // Re-map existing decorations across the transaction's changes
        // so squigglies stay attached when the user types around them.
        let next = value.map(tr.changes);
        for (const effect of tr.effects) {
            if (effect.is(setReferencesEffect)) next = effect.value;
        }
        return next;
    },
    provide: (f) => EditorView.decorations.from(f),
});

const referenceMark = Decoration.mark({ class: 'cm-nl-reference' });

const referencesTheme = EditorView.baseTheme({
    '.cm-nl-reference': {
        backgroundColor: 'rgba(41, 98, 255, 0.18)',
        borderRadius: '2px',
    },
    '&dark .cm-nl-reference': {
        backgroundColor: 'rgba(121, 163, 255, 0.28)',
    },
});

export function lspReferences(client: LspClient): Extension {
    const find = async (view: EditorView, pos: number): Promise<void> => {
        const locs = await client.references(view.state, pos).catch(() => []);
        const inDoc = locs.filter((l: Location) => l.uri === client.uri);
        if (!inDoc.length) {
            // Clear any stale highlights from a previous query.
            view.dispatch({ effects: setReferencesEffect.of(Decoration.none) });
            return;
        }
        const ranges = inDoc.map((l) => lspRangeToCm(view.state.doc, l.range));
        // Decoration ranges must be sorted by `from` per CM's RangeSet
        // invariants; LSP returns locations in document order already,
        // but a defensive sort keeps the extension robust against
        // servers that don't.
        ranges.sort((a, b) => a.from - b.from);
        const decos = ranges.map(({ from, to }) =>
            referenceMark.range(from, Math.max(to, from + 1)),
        );
        view.dispatch({
            effects: setReferencesEffect.of(Decoration.set(decos, true)),
            selection: EditorSelection.cursor(ranges[0]?.from ?? pos),
            scrollIntoView: true,
        });
    };

    const findRefs: Command = (view) => {
        void find(view, view.state.selection.main.head);
        return true;
    };

    const clearRefs: Command = (view) => {
        const field = view.state.field(referencesField, false);
        if (!field || field.size === 0) return false;
        view.dispatch({ effects: setReferencesEffect.of(Decoration.none) });
        return true;
    };

    const keys: KeyBinding[] = [
        { key: 'Shift-F12', run: findRefs },
        // Escape clears the highlights without unsetting the cursor.
        // Returning `false` when there are no highlights lets other
        // Escape handlers (e.g. completion-popup dismiss) run normally.
        { key: 'Escape', run: clearRefs },
    ];

    return [referencesField, referencesTheme, keymap.of(keys)];
}
